/**
 * ProgramApiService (Angular 18) — HTTP wrappers for the
 * /api/event/templates|instances|cancel|trash|resources domains.
 *
 * Replaces the AngularJS legacy service at:
 *   apps/b2b-ui/common/services/api/programApi/programApi.service.js
 *
 * Exposed as `apiService.program.*` through the ApiService facade (downgraded
 * under the legacy name for the 3 b2b-ui consumers).
 *
 * Sub-namespace strategy: **Option A** — every sub-namespace is a `readonly`
 * object literal on the class, grouped with section headers. The criterion
 * for promoting a literal to a separate `@Injectable` sub-service is
 * documented in MIGRATION_GUIDE.md → "Sub-namespace pattern".
 *
 * Notes
 *  - Some endpoints mutate `/v1/` → `/v2/` and others stay on v1. Each URL
 *    is hardcoded so a search from the Network tab lands you here directly.
 *  - `list`, `listOrdered`, `displayOrderCount` and `names` keep the legacy
 *    15s timeout via `rxjs.timeout(15000)`.
 *  - Bug fix: `trash.recoverBulk()` issued `http({...})` (no `$`) in the
 *    legacy and was unreachable at runtime. It is fixed here to PUT
 *    `/v1/api/event/instances/trash/bin/recover` with `{ ids }`.
 */
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { timeout } from "rxjs";

import { AjsResponse, wrapAjs } from "../ajs-response";
import {
  buildParams,
  PAGINATION_FIELDS,
  PAGINATION_SEARCH_FIELDS,
} from "../http-params.util";

// ============= Base URLs =============
// Hardcoded for grep-ability: pasting a URL straight from the browser's
// Network tab into a code search will land you here.

const TEMPLATES_BASE_V2 = "/v2/api/event/templates";
const TEMPLATE_BASE_V1 = "/v1/api/event/template"; // singular for child paths
const TEMPLATE_BASE_V2 = "/v2/api/event/template"; // singular for v2 child paths (displayOrder/count)
const INSTANCES_BASE_V2 = "/v2/api/event/instances"; // plural for v2 (times.list, trash.list)
const INSTANCE_BASE_V1 = "/v1/api/event/instance"; // singular for v1 (instance CRUD)
const INSTANCE_BASE_V2 = "/v2/api/event/instance"; // singular for v2 (resources.instance.*)
const TIMES_BASE_V2 = "/v2/api/event/instance/times";
const TRASH_BASE_V1 = "/v1/api/event/instances/trash/bin";
const TRASH_BASE_V2 = "/v2/api/event/instances/trash/bin";
const CANCEL_BASE_V2 = "/v2/api/event/cancel";
const RESOURCES_REQUESTS_BASE_V2 = "/v2/api/event/resources/requests";

const TEMPLATE_LIST_FIELDS = [
  ...PAGINATION_SEARCH_FIELDS,
  "type",
  "admissionType",
] as const;

const INSTANCE_TIMES_FIELDS = [
  ...PAGINATION_SEARCH_FIELDS,
  "templateId",
  "startTime",
  "endTime",
  "saleChannel",
  "type",
  "filter",
] as const;

const TRASH_LIST_FIELDS = [
  ...PAGINATION_SEARCH_FIELDS,
  "startTime",
  "templateId",
  "endTime",
  "requestFilter",
] as const;

const RESOURCES_REQUESTS_FIELDS = [
  ...PAGINATION_SEARCH_FIELDS,
  "eventId",
  "ownerId",
  "resourceStartTime",
  "resourceEndTime",
  "eventTemplateId",
] as const;

// Note: this endpoint accepts pagination + sort but NOT search.
const SCHEDULE_SUMMARY_FIELDS = [...PAGINATION_FIELDS, "formId"] as const;

const REQUEST_TIMEOUT_MS = 15_000;

// ============= Helpers =============

interface TemplatePayload {
  id?: string | number;
  name: unknown;
  type: unknown;
  description: unknown;
  shortDescription: unknown;
  emailConfirmationMessage: unknown;
  emailCancellationMessage: unknown;
  admissionType: unknown;
  membership: unknown;
  GLCode: unknown;
  customFields: unknown;
  thirdPartyEvent: unknown;
  thirdPartyPercentage: unknown;
  repeatCycle: unknown;
  summary: unknown;
  emailNotify: unknown;
  onlinePaymentsForOrgOrdersEmailNotify: unknown;
  rebookingFee: unknown;
  addOns: unknown;
  images: unknown;
  startTime: unknown;
  endTime: null;
  reviewState: unknown;
  flexPays: unknown;
  schedules: unknown[] | unknown;
  resources: unknown;
  ticketDetails: unknown;
  colorCategory: unknown;
  memberOnlyEvent: unknown;
  allMembersCanPurchase: unknown;
  entitlementConfiguration: unknown;
  prerequisites?: unknown;
  recommendations?: unknown;
  priceLists?: unknown;
}

/**
 * Mirror of the legacy `stripNewSchedules`: when a schedule was just created
 * client-side it carries a transient `NEW: true` marker that must not be sent
 * to the API. Mutates the input (legacy parity).
 */
function stripNewSchedules<T extends { schedules?: unknown }>(data: T): T {
  if (Array.isArray(data.schedules)) {
    for (const sched of data.schedules as Array<Record<
      string,
      unknown
    > | null>) {
      if (sched && Object.prototype.hasOwnProperty.call(sched, "NEW")) {
        delete sched["NEW"];
      }
    }
  }
  return data;
}

/** Curated body builder for `create` / `update`. */
function buildTemplatePayload(
  props: any,
  options: { includeId: boolean; includeRelations: boolean },
): TemplatePayload {
  const out: TemplatePayload = {
    name: props.name,
    type: props.type,
    description: props.description,
    shortDescription: props.shortDescription,
    emailConfirmationMessage: props.emailConfirmationMessage,
    emailCancellationMessage: props.emailCancellationMessage,
    admissionType: props.admissionType || "standard",
    membership: props.membership,
    GLCode: props.GLCode,
    customFields: props.customFields,
    thirdPartyEvent: props.thirdPartyEvent,
    thirdPartyPercentage: props.thirdPartyPercentage,
    repeatCycle: props.repeatCycle,
    summary: props.summary,
    emailNotify: props.emailNotify,
    onlinePaymentsForOrgOrdersEmailNotify:
      props.onlinePaymentsForOrgOrdersEmailNotify,
    rebookingFee: props.rebookingFee,
    addOns: props.addOns,
    images: props.images,
    startTime: props.startTime,
    // Legacy: endTime is always null since the multiple-schedules code was introduced.
    endTime: null,
    reviewState: props.reviewState,
    flexPays: props.flexPays,
    schedules: props.schedules,
    resources: props.resources,
    ticketDetails: props.ticketDetails,
    colorCategory: props.colorCategory,
    memberOnlyEvent: props.memberOnlyEvent,
    allMembersCanPurchase: props.allMembersCanPurchase,
    entitlementConfiguration: props.entitlementConfiguration,
  };
  if (options.includeId) out.id = props.id;
  // `update` also forwards prerequisites + recommendations; `create` does not.
  if (options.includeRelations) {
    out.prerequisites = props.prerequisites;
    out.recommendations = props.recommendations;
  }
  // Legacy priceLists rules:
  //   if reviewState && reviewState !== 'published' → only send when present and first entry has an `id`
  //   else                                            → forward unconditionally
  if (props.reviewState && props.reviewState !== "published") {
    if (
      Array.isArray(props.priceLists) &&
      props.priceLists.length > 0 &&
      props.priceLists[0] &&
      Object.prototype.hasOwnProperty.call(props.priceLists[0], "id")
    ) {
      out.priceLists = props.priceLists;
    }
  } else {
    out.priceLists = props.priceLists;
  }
  return stripNewSchedules(out);
}

interface ScheduleBody {
  id?: string | number;
  name: unknown;
  description: unknown;
  effectiveStartTime: unknown;
  effectiveEndTime: unknown;
  honorHolidays: unknown;
  honorOpenHours: unknown;
  repeatCycle: unknown;
  capacity: unknown;
  duration: unknown;
  weekdays: unknown;
  daysOfMonth: unknown;
  days: unknown;
  monthly: unknown;
  hours: unknown;
  ranges: unknown;
  blockFlexPays: unknown;
  salesChannels: unknown;
  priceLists: unknown;
  resourceRequests: unknown;
  ticketConfig: unknown;
  orderForms: unknown;
  type: unknown;
}

function buildScheduleBody(props: any, includeId: boolean): ScheduleBody {
  const out: ScheduleBody = {
    name: props.name,
    description: props.description,
    effectiveStartTime: props.effectiveStartTime,
    effectiveEndTime: props.effectiveEndTime,
    honorHolidays: props.honorHolidays,
    honorOpenHours: props.honorOpenHours,
    repeatCycle: props.repeatCycle,
    capacity: props.capacity,
    duration: props.duration,
    weekdays: props.weekdays,
    daysOfMonth: props.daysOfMonth,
    days: props.days,
    monthly: props.monthly,
    hours: props.hours,
    ranges: props.ranges,
    blockFlexPays: props.blockFlexPays,
    salesChannels: props.salesChannels,
    priceLists: props.priceLists,
    resourceRequests: props.resourceRequests,
    ticketConfig: props.ticketConfig,
    orderForms: props.orderForms,
    type: props.type,
  };
  if (includeId) out.id = props.id;
  return out;
}

interface FlexpayBody {
  id?: string | number;
  name: unknown;
  type: unknown;
  effectiveStartTime: unknown;
  effectiveEndTime: unknown;
  repeatCycle: unknown;
  weekdays: unknown;
  daysOfMonth: unknown;
  days: unknown;
  monthly: unknown;
  hours: unknown;
  range: unknown;
  discountId: unknown;
}

function buildFlexpayBody(props: any, includeId: boolean): FlexpayBody {
  const out: FlexpayBody = {
    name: props.name,
    type: props.type,
    effectiveStartTime: props.effectiveStartTime,
    effectiveEndTime: props.effectiveEndTime,
    repeatCycle: props.repeatCycle,
    weekdays: props.weekdays,
    daysOfMonth: props.daysOfMonth,
    days: props.days,
    monthly: props.monthly,
    hours: props.hours,
    range: props.range,
    discountId: props.discountId,
  };
  if (includeId) out.id = props.id;
  return out;
}

interface InstanceUpdateBody {
  id: unknown;
  templateId: unknown;
  scheduleId: unknown;
  originalFields: unknown;
  name: unknown;
  description: unknown;
  colorCategory: unknown;
  shortDescription: unknown;
  emailConfirmationMessage: unknown;
  emailCancellationMessage: unknown;
  capacity: unknown;
  GLCode: unknown;
  startTime: unknown;
  endTime: unknown;
  flexPays: unknown;
  resources: unknown;
  resourceDetails: unknown;
  resourceRequests: unknown;
  priceList: unknown;
  rebookingFee: unknown;
  ticketConfig: unknown;
  addOns: unknown;
  customFields: unknown;
  offSchedule: unknown;
  orderForms: unknown;
  sendEmail: unknown;
  organization: unknown;
  salesChannels: unknown;
  ticketDetails?: unknown;
  state?: unknown;
}

function buildInstanceUpdateBody(
  props: any,
  includeStateAndTicketDetails: boolean,
): InstanceUpdateBody {
  const out: InstanceUpdateBody = {
    id: props.id,
    templateId: props.templateId,
    scheduleId: props.scheduleId,
    originalFields: props.originalFields,
    name: props.name,
    description: props.description,
    colorCategory: props.colorCategory,
    shortDescription: props.shortDescription,
    emailConfirmationMessage: props.emailConfirmationMessage,
    emailCancellationMessage: props.emailCancellationMessage,
    capacity: props.capacity,
    GLCode: props.GLCode,
    startTime: props.startTime,
    endTime: props.endTime,
    flexPays: props.flexPays,
    resources: props.resources,
    resourceDetails: props.resourceDetails,
    resourceRequests: props.resourceRequests,
    priceList: props.priceList,
    rebookingFee: props.rebookingFee,
    ticketConfig: props.ticketConfig,
    addOns: props.addOns,
    customFields: props.customFields,
    offSchedule: props.offSchedule,
    orderForms: props.orderForms,
    sendEmail: props.sendEmail,
    organization: props.organization,
    salesChannels: props.salesChannels,
  };
  // `instances.update` also forwards `ticketDetails` and `state`;
  // `instances.updatePrivate` does NOT.
  if (includeStateAndTicketDetails) {
    out.ticketDetails = props.ticketDetails;
    out.state = props.state;
  }
  return out;
}

// ============= Service =============

@Injectable({ providedIn: "root" })
export class ProgramApiService {
  private http = inject(HttpClient);

  // ─── Top-level methods ──────────────────────────────────────────────

  list(
    showPrivate: boolean,
    props?: Record<string, unknown>,
  ): Promise<AjsResponse<any>> {
    const merged = props
      ? { ...props, type: showPrivate ? "private" : "" }
      : undefined;
    return wrapAjs(
      this.http
        .get<any>(`${TEMPLATES_BASE_V2}/summaries`, {
          params: buildParams(merged, TEMPLATE_LIST_FIELDS),
        })
        .pipe(timeout(REQUEST_TIMEOUT_MS)),
    );
  }

  listOrdered(props?: Record<string, unknown>): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http
        .get<any>(`${TEMPLATES_BASE_V2}/displayOrder`, {
          params: buildParams(props, TEMPLATE_LIST_FIELDS),
        })
        .pipe(timeout(REQUEST_TIMEOUT_MS)),
    );
  }

  /**
   * GET /v2/api/event/templates/displayOrder/count
   * GET /v2/api/event/template/:id/displayOrder/count (when eventTemplateId is supplied)
   * The legacy swapped between plural/singular by composing the path manually.
   */
  displayOrderCount(props?: {
    eventTemplateId?: string | number;
  }): Promise<AjsResponse<any>> {
    const url = props?.eventTemplateId
      ? `${TEMPLATE_BASE_V2}/${props.eventTemplateId}/displayOrder/count`
      : `${TEMPLATES_BASE_V2}/displayOrder/count`;
    return wrapAjs(this.http.get<any>(url).pipe(timeout(REQUEST_TIMEOUT_MS)));
  }

  reorder(props: unknown): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.put<any>(`${TEMPLATES_BASE_V2}/displayOrder`, props),
    );
  }

  names(
    showPrivate: boolean,
    props?: Record<string, unknown>,
  ): Promise<AjsResponse<any>> {
    const merged = props
      ? { ...props, type: showPrivate ? "private" : "" }
      : undefined;
    return wrapAjs(
      this.http
        .get<any>(`${TEMPLATES_BASE_V2}/names`, {
          params: buildParams(merged, TEMPLATE_LIST_FIELDS),
        })
        .pipe(timeout(REQUEST_TIMEOUT_MS)),
    );
  }

  create(props: any, publish?: boolean): Promise<AjsResponse<any>> {
    const body = buildTemplatePayload(props, {
      includeId: false,
      includeRelations: false,
    });
    const params = publish
      ? new HttpParams().set("publish", "true")
      : undefined;
    return wrapAjs(this.http.post<any>(TEMPLATES_BASE_V2, body, { params }));
  }

  update(props: any, publish?: boolean): Promise<AjsResponse<any>> {
    const body = buildTemplatePayload(props, {
      includeId: true,
      includeRelations: true,
    });
    const params = publish
      ? new HttpParams().set("publish", "true")
      : undefined;
    return wrapAjs(
      this.http.put<any>(`${TEMPLATES_BASE_V2}/${props.id}`, body, { params }),
    );
  }

  /**
   * Legacy signature: `get(id, noCache, httpCache)`. We preserve `noCache`
   * (adds `?cache=false`) but `httpCache` is intentionally ignored — Angular's
   * `HttpClient` has no $http-cache equivalent.
   */
  get(
    id: string | number,
    noCache?: boolean,
    _httpCache?: boolean,
  ): Promise<AjsResponse<any>> {
    const params = noCache ? new HttpParams().set("cache", "false") : undefined;
    return wrapAjs(
      this.http.get<any>(`${TEMPLATES_BASE_V2}/${id}`, { params }),
    );
  }

  remove(props: {
    id: string | number;
    sendEmail?: boolean;
  }): Promise<AjsResponse<any>> {
    return wrapAjs(this.http.delete<any>(`${TEMPLATES_BASE_V2}/${props.id}`));
  }

  // ─── Template metadata ─────────────────────────────────────────────

  readonly template = {
    published: (props: {
      eventTemplateId: string | number;
    }): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>(
          `${TEMPLATE_BASE_V1}/${props.eventTemplateId}/published`,
        ),
      ),
  };

  // ─── Schedules / flexpay (flat CRUD on /v1/api/event/template) ─────

  readonly schedules = {
    list: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${TEMPLATE_BASE_V1}/${id}/schedules/`)),
    create: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.post<any>(
          `${TEMPLATE_BASE_V1}/${props.eventTemplateId}/schedules/`,
          buildScheduleBody(props, false),
        ),
      ),
    update: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.put<any>(
          `${TEMPLATE_BASE_V1}/${props.eventTemplateId}/schedules/${props.id}`,
          buildScheduleBody(props, true),
        ),
      ),
    remove: (props: {
      eventTemplateId: string | number;
      id: string | number;
    }): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.delete<any>(
          `${TEMPLATE_BASE_V1}/${props.eventTemplateId}/schedules/${props.id}`,
        ),
      ),
  };

  readonly flexpay = {
    list: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${TEMPLATE_BASE_V1}/${id}/flexpay/`)),
    create: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.post<any>(
          `${TEMPLATE_BASE_V1}/${props.eventTemplateId}/flexpay/`,
          buildFlexpayBody(props, false),
        ),
      ),
    update: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.put<any>(
          `${TEMPLATE_BASE_V1}/${props.eventTemplateId}/flexpay/${props.id}`,
          buildFlexpayBody(props, true),
        ),
      ),
    remove: (props: {
      eventTemplateId: string | number;
      id: string | number;
    }): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.delete<any>(
          `${TEMPLATE_BASE_V1}/${props.eventTemplateId}/flexpay/${props.id}`,
        ),
      ),
  };

  // ─── Relationships (prerequisites / dependents / recommendations) ──

  readonly prerequisites = {
    detail: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${TEMPLATE_BASE_V1}/${id}/prerequisites`)),
    create: (
      id: string | number,
      props: { operation: unknown; prerequisites: unknown },
    ): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.post<any>(`${TEMPLATE_BASE_V1}/${id}/prerequisites`, {
          operation: props.operation,
          prerequisites: props.prerequisites,
        }),
      ),
    update: (
      id: string | number,
      props: { operation: unknown; prerequisites: unknown },
    ): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.put<any>(`${TEMPLATE_BASE_V1}/${id}/prerequisites`, {
          operation: props.operation,
          prerequisites: props.prerequisites,
        }),
      ),
    remove: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.delete<any>(`${TEMPLATE_BASE_V1}/${id}/prerequisites`)),
  };

  readonly dependents = {
    list: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${TEMPLATE_BASE_V1}/${id}/dependents`)),
    create: (
      id: string | number,
      props: { dependents: unknown },
    ): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.post<any>(`${TEMPLATE_BASE_V1}/${id}/dependents`, {
          dependents: props.dependents,
        }),
      ),
    update: (
      id: string | number,
      props: { dependents: unknown },
    ): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.put<any>(`${TEMPLATE_BASE_V1}/${id}/dependents`, {
          dependents: props.dependents,
        }),
      ),
    remove: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.delete<any>(`${TEMPLATE_BASE_V1}/${id}/dependents`)),
  };

  readonly recommendations = {
    detail: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${TEMPLATE_BASE_V1}/${id}/recommendations`)),
    create: (
      id: string | number,
      props: { recommendations: unknown; tags: unknown },
    ): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.post<any>(`${TEMPLATE_BASE_V1}/${id}/recommendations`, {
          recommendations: props.recommendations,
          tags: props.tags,
        }),
      ),
    update: (
      id: string | number,
      props: { recommendations: unknown; tags: unknown },
    ): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.put<any>(`${TEMPLATE_BASE_V1}/${id}/recommendations`, {
          recommendations: props.recommendations,
          tags: props.tags,
        }),
      ),
    remove: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.delete<any>(`${TEMPLATE_BASE_V1}/${id}/recommendations`),
      ),
  };

  // ─── Memberships / entitlements (read-only lists) ──────────────────

  readonly memberships = {
    list: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${TEMPLATE_BASE_V1}/${id}/memberships`)),
  };

  readonly entitlements = {
    // Legacy mutates v1 → v2 for this call only; the rest of `memberships` stays on v1.
    list: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${TEMPLATES_BASE_V2}/${id}/entitlements`)),
  };

  // ─── Instances (with nested .times) ────────────────────────────────

  readonly instances = {
    discount: (props: any): Promise<AjsResponse<any>> => {
      const params = buildParams(props, ["accountId", "membershipId"]);
      return wrapAjs(
        this.http.get<any>(`${INSTANCE_BASE_V1}/${props.id}`, { params }),
      );
    },
    detail: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${INSTANCE_BASE_V1}/${id}`)),
    update: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.put<any>(
          INSTANCE_BASE_V1,
          buildInstanceUpdateBody(props, true),
        ),
      ),
    remove: (props: {
      id: string | number;
      sendEmail?: boolean;
    }): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.delete<any>(`${INSTANCE_BASE_V1}/${props.id}`, {
          params: buildParams(props, ["sendEmail"]),
        }),
      ),
    resources: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${INSTANCE_BASE_V1}/${id}/resources`)),
    rebookFees: (ids: string): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>(`${INSTANCE_BASE_V1}/rebook/fees`, {
          params: new HttpParams().set("eventIds", String(ids)),
        }),
      ),
    updatePrivate: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.post<any>(
          `${INSTANCE_BASE_V1}/update`,
          buildInstanceUpdateBody(props, false),
        ),
      ),
    times: {
      // GET /v2/api/event/instances?<INSTANCE_TIMES_FIELDS>
      list: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.get<any>(INSTANCES_BASE_V2, {
            params: buildParams(props, INSTANCE_TIMES_FIELDS),
          }),
        ),
    },
  };

  // ─── Resources (schedule / instance / requests) ─────────────────────

  readonly resources = {
    schedule: {
      list: (props: {
        templateId: string | number;
        scheduleId: string | number;
      }): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.get<any>(
            `${TEMPLATES_BASE_V2}/${props.templateId}/schedules/${props.scheduleId}/resources`,
          ),
        ),
      create: (props: any): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.post<any>(
            `${TEMPLATES_BASE_V2}/${props.templateId}/schedules/${props.scheduleId}/resources`,
            {
              resourceTimeFrame: props.resourceTimeFrame,
              eventResource: props.eventResource,
              notes: props.notes,
              sendEmail: props.sendEmail,
            },
          ),
        ),
      update: (props: any): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.put<any>(
            `${TEMPLATES_BASE_V2}/${props.templateId}/schedules/${props.scheduleId}/resources`,
            {
              id: props.id,
              resourceTimeFrame: props.resourceTimeFrame,
              eventResource: props.eventResource,
              notes: props.notes,
              sendEmail: props.sendEmail,
            },
          ),
        ),
      // Legacy issues DELETE with a request body — uncommon but preserved.
      remove: (props: any): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.delete<any>(
            `${TEMPLATES_BASE_V2}/${props.templateId}/schedules/${props.scheduleId}/resources`,
            {
              body: {
                id: props.id,
                resourceTimeFrame: props.resourceTimeFrame,
                eventResource: props.eventResource,
                notes: props.notes,
                sendEmail: props.sendEmail,
              },
            },
          ),
        ),
      summary: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.get<any>(`${TEMPLATES_BASE_V2}/schedules/summary`, {
            params: buildParams(props, SCHEDULE_SUMMARY_FIELDS),
          }),
        ),
    },
    instance: {
      list: (props: { id: string | number }): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.get<any>(
            `${INSTANCE_BASE_V2}/${props.id}/resourcerequests`,
          ),
        ),
      create: (props: any): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.post<any>(
            `${INSTANCE_BASE_V2}/${props.id}/resourcerequests`,
            {
              resourceTimeFrame: props.resourceTimeFrame,
              eventResource: props.eventResource,
              notes: props.notes,
              sendEmail: props.sendEmail,
            },
          ),
        ),
      // Legacy uses `props.eventId` here (not `props.id`).
      update: (props: any): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.put<any>(
            `${INSTANCE_BASE_V2}/${props.eventId}/resourcerequests`,
            {
              id: props.id,
              resourceTimeFrame: props.resourceTimeFrame,
              eventResource: props.eventResource,
              notes: props.notes,
              sendEmail: props.sendEmail,
            },
          ),
        ),
      remove: (props: {
        id: string | number;
        requestId: string | number;
        sendEmail?: boolean;
      }): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.delete<any>(
            `${INSTANCE_BASE_V2}/${props.id}/resourcerequests/${props.requestId}`,
            {
              params: buildParams(props, ["sendEmail"]),
            },
          ),
        ),
      detail: (props: {
        id: string | number;
        requestId: string | number;
      }): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.get<any>(
            `${INSTANCE_BASE_V2}/${props.id}/resourcerequests/${props.requestId}`,
          ),
        ),
    },
    requests: {
      list: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.get<any>(RESOURCES_REQUESTS_BASE_V2, {
            params: buildParams(props, RESOURCES_REQUESTS_FIELDS),
          }),
        ),
    },
  };

  // ─── Times / trash / cancel ────────────────────────────────────────

  readonly times = {
    detail: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${TIMES_BASE_V2}/${id}`)),
    update: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.put<any>(TIMES_BASE_V2, {
          id: props.id,
          startTime: props.startTime,
          endTime: props.endTime,
          moveResources: props.moveResources,
          duration: props.duration,
          notifyBuyers: props.notifyBuyers,
        }),
      ),
  };

  readonly trash = {
    list: (
      props?: Record<string, unknown>,
      options?: Record<string, unknown>,
    ): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>(TRASH_BASE_V2, {
          params: buildParams(
            { ...(props ?? {}), ...(options ?? {}) },
            TRASH_LIST_FIELDS,
          ),
        }),
      ),
    recover: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.put<any>(`${TRASH_BASE_V1}/recover/${id}`, null)),
    detail: (id: string | number): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${TRASH_BASE_V1}/${id}`)),
    // Bug fix vs legacy: the legacy called `http({...})` (no `$`) so this
    // method always threw a `ReferenceError` at runtime. Fixed here to PUT
    // /v1/api/event/instances/trash/bin/recover with `{ ids }` as the body.
    recoverBulk: (ids: Array<number | string>): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.put<any>(`${TRASH_BASE_V1}/recover`, { ids })),
  };

  readonly cancel = {
    event: (props: {
      id: string | number;
      sendEmail?: boolean;
      sendEmailToResources?: boolean;
      emailMessage?: string;
    }): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.post<any>(CANCEL_BASE_V2, {
          eventId: props.id,
          sendEmail: props.sendEmail,
          sendEmailToResources: props.sendEmailToResources,
          emailMessage: props.emailMessage,
        }),
      ),
  };
}
